import { For, Show, createMemo, createSignal } from 'solid-js';
import { chatStore, ONBOARDING_CONV_ID, isConvUnread } from '~/state/chat';
import AgentCard from '~/components/AgentCard';
import { agentVisualColor } from '~/lib/agent-types';
import { uiStore } from '~/state/ui';
import { loadRailOrder, saveRailOrder } from './chat/rail-order';
import { railTree } from '~/lib/rail-tree';
import { teamPicker } from '~/state/team-picker';

// 2026-06-10 — three responsive layouts (operator request:
// "3 responsive widths con 3 formatos distintos"):
//
//   wide   ≥ NARROW + MEDIUM thresholds → full layout
//   medium between the two              → drop the location chip
//   narrow < NARROW threshold           → first 4-5 letters of name only
//
// Both thresholds were picked by visual inspection on the actual
// layout: at < 80 px even a 5-char monospace name barely fits with
// the row padding; at < 160 px the location chip wraps + jitters.
// 2026-06-10 — narrow threshold lowered to 60 px (was 80). The new
// narrow layout renders the name at the SAME font-size as expanded
// (just truncated to 3 chars + 5 px breathing padding), so we can
// stay in the rich layout for longer before snapping to the
// name-letters-only fallback.
const NARROW_THRESHOLD_PX = 60;
const MEDIUM_THRESHOLD_PX = 160;
// V107.38 — `isService` / `isServiceType` no longer used. The rail's
// ordering algorithm is now agent-type-agnostic (only Master + primary
// architect are special-cased in the head; everything else uses a
// stable creation-order + drag-override body, irrespective of whether
// the conv is a service or a custom worker).

export default function ChatRail() {
  const [order, setOrder] = createSignal<string[]>(loadRailOrder());
  const [dragSrc, setDragSrc] = createSignal<string | null>(null);
  const [dragTgt, setDragTgt] = createSignal<string | null>(null);

  // V107.38 — Rail ordering, rewritten clean. The previous version
  // sorted custom + services + architects EACH by `last_activity_at`,
  // which meant an idle agent jumped slots the moment it started
  // working (operator field report 2026-06-08: A099 was 4th, started
  // working, instantly moved to 2nd). Activity should never reorder
  // the rail — operators rely on positional muscle memory.
  //
  // The model is intentionally two-layer:
  //
  //   HEAD  (always recomputed; positions 0 + optional 1):
  //     - position 0 : Master (`_onboarding_v1`) — pinned
  //     (BT-19: no Orchestrator slot any more — it was retired.)
  //
  //   BODY  (everything else, in a stable order that does NOT move
  //          when activity flips):
  //     - first   : operator's saved drag order, in the order they set
  //     - then    : anything new, by `created_at` ascending — so brand-
  //                 new agents always land at the END of the rail,
  //                 never bubble up past existing entries.
  //
  // `last_activity_at` is no longer read by this memo. Period.
  const orderedConvs = createMemo(() => {
    if (!chatStore.state.convMap[ONBOARDING_CONV_ID]) {
      chatStore.seedOnboardingConv();
    }
    const snapshotConvs = chatStore.state.convs;

    // ── Inventory: every non-archived conv we know about. ──
    // Daemon-authoritative `convs` ∪ locally-created `convMeta`
    // (pre-dispatch convs from the New Agent wizard live only in
    // convMeta until the first dispatch produces a daemon record).
    const allSet = new Set<string>();
    for (const c of Object.keys(snapshotConvs)) {
      if (!snapshotConvs[c]?.archived) allSet.add(c);
    }
    for (const slug of Object.keys(chatStore.state.convMeta)) {
      if (chatStore.state.archivedConvs[slug]) continue;
      // Belt-and-suspenders: respect the daemon snapshot's `archived`
      // flag even if the local `archivedConvs` set hasn't been seeded
      // yet (e.g. the snapshot fetch lagged / hung — ikamiro deadlock,
      // 2026-06-13). Without this, stale localStorage convMeta entries
      // from past sessions (every conv ever seen) flood the rail with
      // already-archived agents until hydration prunes them.
      if (snapshotConvs[slug]?.archived) continue;
      allSet.add(slug);
    }
    allSet.add(ONBOARDING_CONV_ID); // master is always present, even on a fresh cluster
    for (const c of allSet) chatStore.ensureConvMeta(c);

    // ── Head: the Architect only. ──
    // BT-19 — the Orchestrator is retired: a leftover `roadmap-architect-*`
    // conv is plain history in the body (archivable), never a pinned slot.
    const head: string[] = [ONBOARDING_CONV_ID];

    // ── Body: stable, never-reorder-on-activity. ──
    const pinned = new Set<string>(head);

    // (a) Operator's saved drag order, filtered down to slugs still
    //     present (and not in the head).
    const opOrder = order().filter((id) => allSet.has(id) && !pinned.has(id));
    const opSet = new Set(opOrder);

    // (b) Newcomers — anything not in head + not in operator order.
    //     Sort by `created_at` ASCENDING (oldest first → newest at
    //     the bottom). Slug fallback for pre-dispatch convs that
    //     haven't been registered in `convs` yet (their slugs embed
    //     a timestamp anyway, so the order stays stable).
    const ts = (c: string): string => snapshotConvs[c]?.created_at ?? c;
    const newcomers = [...allSet]
      .filter((id) => !pinned.has(id) && !opSet.has(id))
      .sort((a, b) => ts(a).localeCompare(ts(b)));

    return [...head, ...opOrder, ...newcomers];
  });

  // BT-6 — teammates working for another agent hang under it, and leave
  // the rail when they are done (their trace is the wake turn in the
  // parent's thread). The order above still decides the top level.
  const rows = createMemo(() =>
    railTree(orderedConvs(), chatStore.state.convs, {
      root: ONBOARDING_CONV_ID,
      active: chatStore.state.activeConv,
    }),
  );

  const statusOf = (conv: string) => {
    // py-1.11.0 — chat-state-rearchitecture. Single source of truth:
    // the daemon's `live` OR `coordinating` flag on the conv summary.
    // No more OR-ing across heuristics — the daemon computes both
    // server-side and pushes deltas via `conv.activity` WS events.
    const summary = chatStore.state.convs[conv];
    if (!summary) return 'idle' as const;
    return (summary.live || summary.coordinating) ? ('working' as const) : ('idle' as const);
  };

  const drop = (target: string) => {
    const src = dragSrc();
    setDragSrc(null);
    setDragTgt(null);
    if (!src || src === target) return;
    const current = [...orderedConvs()];
    const si = current.indexOf(src);
    if (si < 0) return;
    current.splice(si, 1);
    const ti = current.indexOf(target);
    current.splice(ti >= 0 ? ti : current.length, 0, src);
    setOrder(current);
    saveRailOrder(current);
  };

  const narrow = () => uiStore.state.chatRailWidth < NARROW_THRESHOLD_PX;
  const medium = () =>
    uiStore.state.chatRailWidth >= NARROW_THRESHOLD_PX &&
    uiStore.state.chatRailWidth < MEDIUM_THRESHOLD_PX;
  // Kept under the legacy name so `chat-rail-stack.compact` CSS still
  // matches when the rail is at its narrowest (name-only) layout.
  const compact = narrow;

  // HD-12 — the briefs this member session is holding. One row per
  // member means the rail no longer fills with per-errand convs; what the
  // operator still needs to see is who is queued behind whom.
  const openRequests = (conv: string) =>
    ((chatStore.state.convs[conv] as { open_requests?: Array<{ parent_label?: string; parent_conv?: string; brief?: string }> } | undefined)
      ?.open_requests) ?? [];

  return (
    <aside class={`chat-rail-stack ${compact() ? 'compact' : ''}`}>
      {/* 2026-06-19 — the rail header (Agents + "+") moved up into the
          chat column's top bar (ChatColumn). The rail is just the list. */}
      <div class="flex-1 min-h-0 overflow-y-auto chat-rail-pinned chat-rail-list">
        <For each={rows()}>
          {(row) => {
            const c = row.conv;
            const meta = () => chatStore.ensureConvMeta(c);
            const card = () => (
              <AgentCard
                conv={c}
                meta={meta()}
                active={chatStore.state.activeConv === c}
                status={statusOf(c)}
                pendingReview={isConvUnread(c)}
                waiting={openRequests(c)}
                stripe={agentVisualColor(c, meta())}
                compact={compact()}
                medium={medium()}
                railWidth={uiStore.state.chatRailWidth}
                onSelect={(id) => { teamPicker.close(); chatStore.setActiveConv(id); }}
                onDragStart={(id) => setDragSrc(id)}
                onDragEnd={() => { setDragSrc(null); setDragTgt(null); }}
                onDragOver={(id) => setDragTgt(id)}
                onDragLeave={(id) => { if (dragTgt() === id) setDragTgt(null); }}
                onDrop={(id) => drop(id)}
                dragOver={dragTgt() === c && dragSrc() !== null && dragSrc() !== c}
                dragging={dragSrc() === c}
              />
            );
            return (
              <Show when={row.depth > 0} fallback={card()}>
                {/* dynamic: nesting depth drives the indent + connector. */}
                <div class="rail-child" style={{ '--rail-depth': String(row.depth) }}>
                  {card()}
                </div>
              </Show>
            );
          }}
        </For>
        <Show when={orderedConvs().length === 0}>
          <button
            type="button"
            onClick={() => {
              // V46 onboarding flow: clicking the synthetic Coordinator
              // card seeds + activates the onboarding conv so the chat
              // opens with the welcome bubble + composer. The "+" button
              // up top is the wizard path; the empty-state card is the
              // "talk to the coordinator now" shortcut.
              chatStore.seedOnboardingConv();
              chatStore.setActiveConv(ONBOARDING_CONV_ID);
            }}
            class="text-left rounded-md border border-dashed border-emerald-500/35 bg-emerald-500/5 px-3 py-3 hover:border-emerald-500/55"
            /* dynamic: stripe colour pulled from the agent-type registry.
               This CTA seeds the onboarding conv, so use the master-architect
               pink — matches what the operator will see right after clicking. */
            style={{ 'border-left': `3px solid ${agentVisualColor(ONBOARDING_CONV_ID, null)}` }}
            title="Talk to the Coordinator — kicks off the project's roadmap"
          >
            <div class="text-[11px] font-mono text-emerald-300 mb-1">⬢ General coder</div>
            <div class="text-[11px] text-gray-400 leading-snug">
              No agents yet. Click here to talk to the Coordinator and scaffold the roadmap, or use ＋ above to create a typed agent.
            </div>
          </button>
        </Show>
      </div>
    </aside>
  );
}
